import { Dialog, DialogContent, DialogHeader, DialogTitle } from "../ui/dialog";
import { Button } from "../ui/button";
import { ComplexityChip, StatusChip } from "./ShelfChips";
import { Hand } from "lucide-react";

const ACTION_LABELS = {
  create: "Put on the shelf",
  claim: "Claimed",
  release: "Handed back",
  status: "Moved",
  edit: "Edited",
};

const Row = ({ label, children }) => (
  <div className="flex gap-4 py-2 border-b border-border/60 last:border-0">
    <span className="w-32 shrink-0 text-[11px] uppercase tracking-wider text-muted-foreground pt-0.5">
      {label}
    </span>
    <span className="text-sm text-foreground min-w-0 break-words">{children}</span>
  </div>
);

export const ShelfDetailDialog = ({ item, open, onOpenChange, onClaim, canClaim, busy }) => {
  if (!item) return null;
  const due = item.days_until_due;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-xl max-h-[90vh] overflow-y-auto" data-testid="shelf-detail-dialog">
        <DialogHeader>
          <DialogTitle className="text-xl leading-snug pr-8 break-words">
            {item.client_id && (
              <span className="font-mono text-muted-foreground mr-2">{item.client_id}</span>
            )}
            {item.client_name}
          </DialogTitle>
          <div className="flex items-center gap-2 pt-1">
            <StatusChip value={item.status} />
            <ComplexityChip value={item.complexity} />
          </div>
        </DialogHeader>

        <div className="-mt-1">
          <Row label="Work">{item.work_label}</Row>
          <Row label="Due">
            {item.due_date ? (
              <span className={due < 0 ? "text-rose-500" : due <= 14 ? "text-amber-500" : ""}>
                {item.due_date}
                {due !== null && (due < 0 ? ` · ${Math.abs(due)} days late` : ` · ${due} days out`)}
              </span>
            ) : (
              <span className="text-muted-foreground">No due date</span>
            )}
          </Row>
          {item.est_hours != null && <Row label="Est. hours">{item.est_hours}</Row>}
          <Row label="Put up by">{item.created_by}</Row>
          <Row label="On shelf">{item.days_on_shelf} days</Row>
          {item.claimed_by_name && <Row label="Claimed by">{item.claimed_by_name}</Row>}
          <Row label="Notes">
            {item.notes ? (
              <span className="whitespace-pre-wrap">{item.notes}</span>
            ) : (
              <span className="text-muted-foreground">None</span>
            )}
          </Row>
        </div>

        {item.history?.length > 0 && (
          <div className="space-y-1.5">
            <p className="text-[11px] uppercase tracking-wider text-muted-foreground">History</p>
            {item.history.map((h, i) => (
              <div key={i} className="flex items-baseline gap-2 text-xs">
                <span className="text-muted-foreground tabular-nums shrink-0">
                  {new Date(h.ts).toLocaleDateString(undefined, { month: "short", day: "numeric" })}
                </span>
                <span className="text-foreground">
                  {ACTION_LABELS[h.action] || h.action}
                  {h.to && h.action === "status" ? ` to ${h.to.replace(/_/g, " ")}` : ""} — {h.actor}
                  {h.reason ? ` (${h.reason})` : ""}
                </span>
              </div>
            ))}
          </div>
        )}

        {item.status === "ready" && (
          <Button
            className="w-full gap-2"
            disabled={!canClaim || busy}
            onClick={() => onClaim(item)}
            data-testid="detail-claim-btn"
          >
            <Hand size={15} />
            {busy ? "Claiming…" : canClaim ? "Claim this" : "You're at your limit"}
          </Button>
        )}
      </DialogContent>
    </Dialog>
  );
};
